import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { apiCall } from "../config/api";

const TemplatesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [keyValue, setKeyValue] = useState("");
  const [localesValue, setLocalesValue] = useState(`{
  "en": { "title": "Update", "body": "Updated today" },
  "tr": { "title": "Güncelleme", "body": "Bugün güncellendi" }
}`);
  const [singleTitle, setSingleTitle] = useState("");
  const [singleBody, setSingleBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const response = await apiCall("/api/templates", {
        method: "GET",
        params: { limit: 200 },
      });
      setItems(response.data.templates || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setKeyValue("");
    setLocalesValue(`{
  "en": { "title": "Update", "body": "Updated today" },
  "tr": { "title": "Güncelleme", "body": "Bugün güncellendi" }
}`);
    setSingleTitle("");
    setSingleBody("");
    setOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setKeyValue(tpl.key || "");
    setLocalesValue(JSON.stringify(tpl.locales || {}, null, 2));
    setSingleTitle("");
    setSingleBody("");
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const locales = JSON.parse(localesValue);
      const payload = { key: keyValue, locales };

      if (editing) {
        await apiCall(`/api/templates/${editing.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await apiCall("/api/templates", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
    } catch (e) {
      return alert("Hata: " + (e.message || e));
    }
    setOpen(false);
    await load();
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm("Silinsin mi?")) return;
    try {
      await apiCall(`/api/templates/${tpl.id}`, {
        method: "DELETE",
      });
      await load();
    } catch (e) {
      alert("Hata: " + (e.message || e));
    }
  };

  const generateLocalesFromSingle = () => {
    const langs = ["tr", "ru", "de", "fr", "es", "pt", "en"];
    const obj = {};
    langs.forEach((l) => {
      obj[l] = { title: singleTitle || "", body: singleBody || "" };
    });
    setLocalesValue(JSON.stringify(obj, null, 2));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h4">📄 Şablonlar</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" onClick={openCreate}>
            Yeni Şablon
          </Button>
        </Box>
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Diller</TableCell>
              <TableCell align="right">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((tpl) => (
              <TableRow key={tpl.id} hover>
                <TableCell>{tpl.key}</TableCell>
                <TableCell>
                  {Object.keys(tpl.locales || {}).join(", ")}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(tpl)}>
                    <EditOutlinedIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(tpl)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Kayıt yok
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editing ? "Şablonu Düzenle" : "Yeni Şablon"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              fullWidth
              label="Key"
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
            />
            <Box>
              <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                Tek metinden 7 dil üret
              </Typography>
              <TextField
                fullWidth
                label="Başlık (tek metin)"
                value={singleTitle}
                onChange={(e) => setSingleTitle(e.target.value)}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                label="İçerik (tek metin)"
                multiline
                rows={3}
                value={singleBody}
                onChange={(e) => setSingleBody(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={generateLocalesFromSingle}
              >
                7 dil oluştur (tr,ru,de,fr,es,pt,en)
              </Button>
            </Box>
            <TextField
              fullWidth
              label="Locales (JSON)"
              multiline
              rows={12}
              value={localesValue}
              onChange={(e) => setLocalesValue(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleSave}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplatesPage;
